import type { AssetClassBranch } from '@interfaces/financialProduct';
import {
  classTargetPercentageForModelChild,
  reconciledPortfolioPercentagesForModel,
  resolvePortfolioPercentageForModelChild,
} from '@ovComponents/3-pattern/securitiesSelectionTable/fundTargetPercentage';
import { isHouseholdModelPortfolioAssetClassStrategy } from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.allocation.utils';
import type {
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioModel,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { round, sumBy } from 'lodash-es';

export type AllocationComparisonRow = {
  /** The security the weights on this row belong to. */
  financialProductId: string;
  name: string;
  ticker?: string;
  assetClassName?: string;
  /** Absent on a holding the household does not have today. */
  current?: number;
  /** Absent on a holding the change takes away. */
  proposed?: number;
  /**
   * Portfolio-wide weight used to price the Current ($) / Proposed ($) columns.
   * The group-reconciled 2 dp share the table shows, so a row's money is its
   * displayed % of the balance. Falls back to
   * unrounded within-class × branch when the row has no reconciled share.
   */
  currentShare?: number;
  proposedShare?: number;
  /** Inline portfolio-wide % beside within-class weight — display only. */
  currentPortfolioWide?: number;
  proposedPortfolioWide?: number;
};

export type AssetClassBranchComparisonRow = {
  assetClassId: string;
  name: string;
  color?: AssetClassBranch['color'];
  current?: number | null;
  proposed?: number | null;
};

const branchNameOf = (
  translateBackendString: (translatedObject: unknown) => string,
  branch: AssetClassBranch,
): string =>
  translateBackendString(branch.assetClass?.translatedName) ||
  branch.assetClass?.name ||
  '';

const nameOf = (
  translateBackendString: (translatedObject: unknown) => string,
  child: HouseholdModelPortfolioChild,
): string =>
  translateBackendString(child.financialProduct?.translatedName) ||
  child.financialProduct?.name ||
  '';

const assetClassNameOf = (
  translateBackendString: (translatedObject: unknown) => string,
  child: HouseholdModelPortfolioChild,
): string | undefined => {
  const assetClass =
    child.assetClass ?? child.financialProduct?.primaryAssetClass;
  return assetClass
    ? translateBackendString(assetClass.translatedName) || assetClass.name
    : undefined;
};

const byProduct = (
  model: HouseholdModelPortfolioModel | undefined,
): Map<string, HouseholdModelPortfolioChild> =>
  new Map(
    (model?.children ?? [])
      .filter((child) => !!child.financialProduct?.id)
      .map((child) => [child.financialProduct?.id as string, child]),
  );

const portfolioShareForSide = (
  child: HouseholdModelPortfolioChild | undefined,
  model: HouseholdModelPortfolioModel | undefined,
  reconciledByProduct: Map<string, number>,
  withinClassOverride?: number | null,
): number | undefined => {
  if (!child) return undefined;

  const reconciled = child.financialProduct?.id
    ? reconciledByProduct.get(child.financialProduct.id)
    : undefined;
  if (reconciled != null) return reconciled;

  const withinClass = withinClassOverride ?? child.percentage;
  if (withinClass == null || !Number.isFinite(withinClass)) {
    return undefined;
  }

  if (!isHouseholdModelPortfolioAssetClassStrategy(model)) {
    return withinClass;
  }

  const classTarget = classTargetPercentageForModelChild(child, model);
  if (classTarget == null) return undefined;

  return (withinClass / 100) * classTarget;
};

/**
 * 2 dp portfolio-wide % for the table and IPS. Grouped models read the
 * group-reconciled share so a class's rows sum to its target; otherwise see
 * {@link resolvePortfolioPercentageForModelChild}.
 */
const portfolioWideForDisplay = (
  child: HouseholdModelPortfolioChild | undefined,
  model: HouseholdModelPortfolioModel | undefined,
  reconciledByProduct: Map<string, number>,
  {
    withinClassOverride,
    allowEffectiveFallback = true,
  }: {
    withinClassOverride?: number | null;
    allowEffectiveFallback?: boolean;
  } = {},
): number | undefined => {
  if (!child) return undefined;

  const reconciled = child.financialProduct?.id
    ? reconciledByProduct.get(child.financialProduct.id)
    : undefined;
  if (reconciled != null) return reconciled;

  return (
    resolvePortfolioPercentageForModelChild(
      child,
      model,
      withinClassOverride,
      allowEffectiveFallback,
    ) ?? undefined
  );
};

/**
 * The two models as one list of holdings, so a weight can be read against the
 * weight it replaces rather than found again in a second table.
 *
 * The proposal leads, in the order the advisor left it. What the household
 * holds today and the change does not carry forward follows, weightless on the
 * proposed side — a holding being taken away is as much a part of the change as
 * one being added, and a reader who only saw the proposal would miss it.
 */
export const allocationComparisonRows = (
  translateBackendString: (translatedObject: unknown) => string,
  proposed: HouseholdModelPortfolioModel,
  current?: HouseholdModelPortfolioModel,
): AllocationComparisonRow[] => {
  const held = byProduct(current);
  const reconciledCurrent = reconciledPortfolioPercentagesForModel(
    current?.children,
    current,
  );
  const reconciledProposed = reconciledPortfolioPercentagesForModel(
    (proposed.children ?? []).map((child) => ({
      ...child,
      percentage: child.percentage ?? 0,
    })),
    proposed,
  );
  const rows: AllocationComparisonRow[] = (proposed.children ?? [])
    .filter((child) => !!child.financialProduct?.id)
    .map((child) => {
      const financialProductId = child.financialProduct?.id as string;
      const today = held.get(financialProductId);

      return {
        financialProductId,
        name:
          nameOf(translateBackendString, child) ||
          (today ? nameOf(translateBackendString, today) : ''),
        ticker: child.financialProduct?.ticker ?? undefined,
        assetClassName: assetClassNameOf(translateBackendString, child),
        current: today?.percentage,
        proposed: child.percentage ?? 0,
        currentShare: portfolioShareForSide(today, current, reconciledCurrent),
        proposedShare: portfolioShareForSide(
          child,
          proposed,
          reconciledProposed,
          child.percentage ?? 0,
        ),
        currentPortfolioWide: portfolioWideForDisplay(
          today,
          current,
          reconciledCurrent,
        ),
        proposedPortfolioWide: portfolioWideForDisplay(
          child,
          proposed,
          reconciledProposed,
          { withinClassOverride: child.percentage ?? 0 },
        ),
      };
    });

  const proposedIds = new Set(rows.map((row) => row.financialProductId));

  return [
    ...rows,
    ...[...held.entries()]
      .filter(([financialProductId]) => !proposedIds.has(financialProductId))
      .map(([financialProductId, child]) => ({
        financialProductId,
        name: nameOf(translateBackendString, child),
        ticker: child.financialProduct?.ticker ?? undefined,
        assetClassName: assetClassNameOf(translateBackendString, child),
        current: child.percentage,
        proposed: undefined,
        currentShare: portfolioShareForSide(child, current, reconciledCurrent),
        proposedShare: undefined,
        currentPortfolioWide: portfolioWideForDisplay(
          child,
          current,
          reconciledCurrent,
        ),
        proposedPortfolioWide: undefined,
      })),
  ];
};

/**
 * How much of the portfolio each asset class is targeted for, read the same
 * way on both sides of the comparison as the holdings table below.
 */
export const assetClassBranchComparisonRows = (
  translateBackendString: (translatedObject: unknown) => string,
  proposed: HouseholdModelPortfolioModel,
  current?: HouseholdModelPortfolioModel,
): AssetClassBranchComparisonRow[] => {
  if (!isHouseholdModelPortfolioAssetClassStrategy(proposed)) {
    return [];
  }

  const currentByClassId = new Map(
    (current?.assetClassBranches ?? [])
      .filter((branch) => !!branch.assetClass?.id)
      .map((branch) => [branch.assetClass?.id as string, branch]),
  );

  const rows: AssetClassBranchComparisonRow[] = (
    proposed.assetClassBranches ?? []
  )
    .filter((branch) => !!branch.assetClass?.id)
    .map((branch) => {
      const assetClassId = branch.assetClass?.id as string;
      const today = currentByClassId.get(assetClassId);

      return {
        assetClassId,
        name:
          branchNameOf(translateBackendString, branch) ||
          (today ? branchNameOf(translateBackendString, today) : ''),
        color: branch.color ?? today?.color,
        current: today?.targetPercentage ?? undefined,
        proposed: branch.targetPercentage ?? undefined,
      };
    });

  const proposedClassIds = new Set(rows.map((row) => row.assetClassId));

  return [
    ...rows,
    ...[...currentByClassId.entries()]
      .filter(([assetClassId]) => !proposedClassIds.has(assetClassId))
      .map(([assetClassId, branch]) => ({
        assetClassId,
        name: branchNameOf(translateBackendString, branch),
        color: branch.color,
        current: branch.targetPercentage ?? undefined,
        proposed: undefined,
      })),
  ];
};

export const assetClassBranchDelta = (
  row: AssetClassBranchComparisonRow,
): number | undefined => {
  if (row.current == null || row.proposed == null) return undefined;

  return round(row.proposed - row.current, 2);
};

/** What the change does to a holding, read from the two weights on its row. */
export type AllocationChange =
  | 'ADDED'
  | 'REMOVED'
  | 'INCREASED'
  | 'DECREASED'
  | 'UNCHANGED';

/**
 * What the proposal adds to a holding's weight, in percentage points.
 *
 * Rounded the way the table reads them, so a difference too small to be shown
 * is not one the advisor is asked to look for.
 */
export const allocationDelta = (row: AllocationComparisonRow): number =>
  round((row.proposed ?? 0) - (row.current ?? 0), 2);

/** Portfolio-wide counterpart to {@link allocationDelta} — display only. */
export const allocationPortfolioWideDelta = (
  row: AllocationComparisonRow,
): number | undefined => {
  if (row.currentPortfolioWide == null && row.proposedPortfolioWide == null) {
    return undefined;
  }

  return round(
    (row.proposedPortfolioWide ?? 0) - (row.currentPortfolioWide ?? 0),
    2,
  );
};

/** Which percentage the review table is showing for asset-class models. */
export type AllocationPercentageView = 'portfolio' | 'assetClass';

export const allocationDisplayWeight = (
  row: AllocationComparisonRow,
  side: 'current' | 'proposed',
  view: AllocationPercentageView,
): number | undefined => {
  if (view === 'portfolio') {
    return side === 'current'
      ? row.currentPortfolioWide
      : row.proposedPortfolioWide;
  }

  return side === 'current' ? row.current : row.proposed;
};

export const allocationDisplayDelta = (
  row: AllocationComparisonRow,
  view: AllocationPercentageView,
): number => {
  if (view === 'portfolio') {
    return allocationPortfolioWideDelta(row) ?? allocationDelta(row);
  }

  return allocationDelta(row);
};

export const isAllocationChangedForView = (
  row: AllocationComparisonRow,
  view: AllocationPercentageView,
): boolean => {
  if (view === 'assetClass') {
    return isAllocationChanged(row);
  }

  if (row.current == null || row.proposed == null) {
    return isAllocationChanged(row);
  }

  const delta = allocationPortfolioWideDelta(row);
  if (delta == null) {
    return isAllocationChanged(row);
  }

  return delta !== 0;
};

export const changedAllocationCountForView = (
  rows: AllocationComparisonRow[],
  view: AllocationPercentageView,
): number => rows.filter((row) => isAllocationChangedForView(row, view)).length;

export const allocationChange = (
  row: AllocationComparisonRow,
): AllocationChange => {
  if (row.current == null) return 'ADDED';
  if (row.proposed == null) return 'REMOVED';

  const delta = allocationDelta(row);
  if (delta > 0) return 'INCREASED';
  if (delta < 0) return 'DECREASED';
  return 'UNCHANGED';
};

export const isAllocationChanged = (row: AllocationComparisonRow): boolean =>
  allocationChange(row) !== 'UNCHANGED';

/** How many of the holdings the change actually touches. */
export const changedAllocationCount = (
  rows: AllocationComparisonRow[],
): number => rows.filter(isAllocationChanged).length;

/** What the weights on one side of the comparison come to. */
export const allocationTotal = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
): number =>
  round(
    sumBy(rows, (row) => row[side] ?? 0),
    2,
  );

/**
 * What a target weight is worth against the household's balance.
 *
 * A model carries weights and nothing else, so the money beside one is that
 * share of what the household holds today. Both sides of the comparison are
 * read against the same balance, which is what lets the two money columns be
 * read as a change rather than as two unrelated valuations.
 *
 * The weight has to be one read against the whole model: a grouped model's
 * holding weights are read within their group, and are a share of the balance
 * only once scaled by the group's own target.
 */
export const allocationValueCents = (
  percentage: number | undefined,
  householdMarketValueCents: number | undefined,
): number | undefined =>
  percentage == null || householdMarketValueCents == null
    ? undefined
    : Math.round((percentage / 100) * householdMarketValueCents);

/** The weight a side's money is read from, which is its share of the model. */
const SHARE_OF: Record<
  'current' | 'proposed',
  'currentShare' | 'proposedShare'
> = {
  current: 'currentShare',
  proposed: 'proposedShare',
};

/**
 * Share the household balance across a side's weights so the cents beside each
 * holding add up to the figure under the column.
 *
 * It is the share of the whole model that is read, not the weight the table
 * shows: on a grouped model those are only the same figure once the group's own
 * target is taken into account, and the weight alone would value every holding
 * as though its group were the entire portfolio.
 *
 * When the shares come to 100%, that figure is the balance itself — so the
 * Current balance line and the Current ($) total say the same thing. When they
 * do not, the total is only that share of the balance, which is what the column
 * is showing.
 */
export const allocationSideValueCents = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
  householdMarketValueCents: number | undefined,
): (number | undefined)[] => {
  if (householdMarketValueCents == null) {
    return rows.map(() => undefined);
  }

  const percentages = rows.map((row) => row[SHARE_OF[side]]);
  const totalPercentage = round(
    sumBy(percentages, (percentage) => percentage ?? 0),
    2,
  );
  const targetCents =
    totalPercentage === 100
      ? householdMarketValueCents
      : Math.round((totalPercentage / 100) * householdMarketValueCents);

  const exact = percentages.map((percentage) =>
    percentage == null
      ? undefined
      : (percentage / 100) * householdMarketValueCents,
  );
  const values = exact.map((value) =>
    value == null ? undefined : Math.floor(value),
  );
  let remaining = targetCents - sumBy(values, (value) => value ?? 0);

  const order = values
    .map((_value, index) => ({
      index,
      fraction:
        exact[index] == null ? -1 : exact[index]! - Math.floor(exact[index]!),
    }))
    .filter(({ index }) => values[index] != null)
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  if (remaining > 0) {
    for (const { index } of order) {
      if (remaining === 0) break;
      values[index] = (values[index] ?? 0) + 1;
      remaining -= 1;
    }
  } else if (remaining < 0) {
    for (const { index } of [...order].reverse()) {
      if (remaining === 0) break;
      values[index] = (values[index] ?? 0) - 1;
      remaining += 1;
    }
  }

  return values;
};

/**
 * What one side of the comparison is worth, totalled over the same figures the
 * column shows so the column adds up to what is under it.
 */
export const allocationTotalValueCents = (
  rows: AllocationComparisonRow[],
  side: 'current' | 'proposed',
  householdMarketValueCents: number | undefined,
): number | undefined =>
  householdMarketValueCents == null
    ? undefined
    : sumBy(
        allocationSideValueCents(rows, side, householdMarketValueCents),
        (value) => value ?? 0,
      );
